/**
 * auto-captions-voice-dubbing.js — browser-only multi-language voice DUBBING
 * for the translate pipeline of /auto-captions.
 *
 * Etalon-only — every competitor (Submagic, CapCut, Captions.ai, Veed) sends
 * the user's audio to a cloud TTS / voice-clone service. We do it on-device.
 *
 * Stack
 * -----
 *   - Piper TTS WASM via window.PiperEngine — already shipped for
 *     /document-reader (8 voices: en, ru, de, fr, es, pt, ar, id; ko falls
 *     back to Web Speech via PiperEngine itself).
 *   - AudioPipeline.stretch (Signalsmith Stretch, MIT, off-thread worker) — the
 *     site-wide time-stretch engine. Used here for FORMANT-aware
 *     time-stretching so each translated sentence fits exactly into its
 *     original word-timing window without resampling-pitch-up artefacts.
 *
 * Phase-1 disclosure (read this before touching `speaker_timbre`):
 *   Phase-1 keeps speaker timbre OFF. The user hears the Piper voice
 *   (a different speaker), not their own voice translated. This is honest
 *   technology — we do NOT clone the user's voice. The UI surfaces this
 *   verbatim via the `voice_dubbing_timbre_disclosure` i18n key, and the
 *   only "voice" the dub uses is the Piper voice the user picked.
 *
 * Pipeline integration (read this before touching the burn path):
 *   The pipeline calls window.__acVoiceDubbing.synthesize(words, target) to
 *   render one AudioBuffer per sentence, applyTimestretch() to fit each
 *   sentence into its original time window, and mixDownToOriginal() to mix
 *   the dubbed bus over the original video audio (ducked by -15 dB by
 *   default). The mixed PCM is encoded to WAV and written to FFmpeg's
 *   virtual FS as an extra input; the burn FFmpeg invocation maps the
 *   dubbed track via `-map 1:a` instead of `-map 0:a?`.
 *
 * Public surface
 * --------------
 *   window.__acVoiceDubbing.isEnabled()                       → bool
 *   window.__acVoiceDubbing.getVoice()                        → voiceKey or null
 *   window.__acVoiceDubbing.setVoice(voiceKey)                — persists via __toolSettings
 *   window.__acVoiceDubbing.availableVoices(targetLangKey)    → [{key,label,langKey}]
 *   window.__acVoiceDubbing.synthesize(words, targetLang, opts) → Promise<AudioBuffer>
 *   window.__acVoiceDubbing.applyTimestretch(buffer, targetSec) → Promise<AudioBuffer>
 *   window.__acVoiceDubbing.mixDownToOriginal(origBuf, ttsBuf, duckDb) → Promise<AudioBuffer>
 *   window.__acVoiceDubbing.buildBurnAudioFilter()            → ffmpeg `-af` chain
 *   window.__acVoiceDubbing.encodeWav(audioBuffer)            → Uint8Array (16-bit PCM)
 *
 * The pipeline calls these as needed; this file owns NO DOM beyond reading the
 * toggle/select that the blade renders inside the translate card.
 *
 * Memory notes
 * ------------
 *   Piper voices are 30–150 MB each — Service Worker CacheFirst keeps them
 *   warm across sessions (see public/sw.js). We lazy-load on the FIRST call
 *   to synthesize(), NOT on the toggle flip — flipping the checkbox to peek
 *   shouldn't blow 80 MB of bandwidth.
 */
(function () {
    'use strict';

    if (window.__acVoiceDubbing) return;

    var TOOL_SLUG = 'auto-captions';
    var SETTING_KEY_TOGGLE = 'voice_dubbing_on';
    var SETTING_KEY_VOICE  = 'voice_dubbing_voice';
    var SETTING_KEY_DUCK   = 'voice_dubbing_duck_db';
    var DEFAULT_DUCK_DB    = -15;

    // ────────────────────────────────────────────────────────────────────
    // Voice catalogue — Piper-backed only. `langKey` is the Piper voice
    // resolver key (matches PiperEngine.VOICES). `targetLang` is the
    // auto-captions translate target the voice can dub for.
    //
    // Korean intentionally omitted — Piper has no Korean voice (see
    // PiperEngine.js comment line ~61). When user picks ko as translate
    // target we surface "No on-device voice available" + the toggle stays
    // off. Users keep the Web Speech / cloud fallbacks they always had.
    // ────────────────────────────────────────────────────────────────────
    var VOICE_CATALOGUE = [
        { key: 'en',        targetLang: 'en', label: 'Amy (US, female)',     langKey: 'en' },
        { key: 'en-ryan',   targetLang: 'en', label: 'Ryan (US, male)',      langKey: 'en-ryan' },
        { key: 'ru',        targetLang: 'ru', label: 'Irina (female)',       langKey: 'ru' },
        { key: 'ru-dmitri', targetLang: 'ru', label: 'Dmitri (male)',        langKey: 'ru-dmitri' },
        { key: 'de',        targetLang: 'de', label: 'Thorsten (male)',      langKey: 'de' },
        { key: 'fr',        targetLang: 'fr', label: 'Siwis (female)',       langKey: 'fr' },
        { key: 'fr-tom',    targetLang: 'fr', label: 'Tom (male)',           langKey: 'fr-tom' },
        { key: 'es',        targetLang: 'es', label: 'Davefx (ES, male)',    langKey: 'es' },
        { key: 'pt',        targetLang: 'pt', label: 'Cadu (BR, male)',      langKey: 'pt' },
        { key: 'ar',        targetLang: 'ar', label: 'Kareem (male)',        langKey: 'ar' },
        { key: 'id',        targetLang: 'id', label: 'News (ID, neutral)',   langKey: 'id' }
    ];

    function availableVoices(targetLang) {
        if (!targetLang) return VOICE_CATALOGUE.slice();
        return VOICE_CATALOGUE.filter(function (v) { return v.targetLang === targetLang; });
    }

    function isPiperReady() {
        return !!(window.PiperEngine && typeof window.PiperEngine.synthesize === 'function');
    }
    function isRubberbandReady() {
        return !!(window.AudioPipeline && typeof window.AudioPipeline.stretch === 'function');
    }

    // ────────────────────────────────────────────────────────────────────
    // Toggle + voice persistence — uses window.__toolSettings when present
    // so the user's pick survives reload. In a Node test harness those APIs
    // are absent; we degrade to an in-memory cache so the tests still pass.
    // ────────────────────────────────────────────────────────────────────
    var _memory = { on: false, voice: null, duckDb: DEFAULT_DUCK_DB };

    function readSetting(key, fallback) {
        if (window.__toolSettings && typeof window.__toolSettings.load === 'function') {
            try { return window.__toolSettings.load(TOOL_SLUG, key, fallback); } catch (_) {}
        }
        if (Object.prototype.hasOwnProperty.call(_memory, _settingMemKey(key))) {
            var v = _memory[_settingMemKey(key)];
            return v == null ? fallback : v;
        }
        return fallback;
    }
    function writeSetting(key, value) {
        if (window.__toolSettings && typeof window.__toolSettings.save === 'function') {
            try { window.__toolSettings.save(TOOL_SLUG, key, value); } catch (_) {}
        }
        _memory[_settingMemKey(key)] = value;
    }
    function _settingMemKey(k) {
        if (k === SETTING_KEY_TOGGLE) return 'on';
        if (k === SETTING_KEY_VOICE)  return 'voice';
        if (k === SETTING_KEY_DUCK)   return 'duckDb';
        return k;
    }

    function isEnabled() {
        // Read DOM toggle when present so the pipeline picks up live changes
        // without us having to bind a change handler from this module.
        var dom = (typeof document !== 'undefined') ? document.getElementById('ac-voice-dub-toggle') : null;
        if (dom && typeof dom.checked === 'boolean') return !!dom.checked;
        return !!readSetting(SETTING_KEY_TOGGLE, false);
    }
    function setEnabled(on) {
        writeSetting(SETTING_KEY_TOGGLE, !!on);
        var dom = (typeof document !== 'undefined') ? document.getElementById('ac-voice-dub-toggle') : null;
        if (dom && typeof dom.checked === 'boolean') dom.checked = !!on;
    }

    function getVoice() {
        var dom = (typeof document !== 'undefined') ? document.getElementById('ac-voice-dub-voice') : null;
        if (dom && dom.value) return dom.value;
        return readSetting(SETTING_KEY_VOICE, null);
    }
    function setVoice(voiceKey) {
        writeSetting(SETTING_KEY_VOICE, voiceKey);
        var dom = (typeof document !== 'undefined') ? document.getElementById('ac-voice-dub-voice') : null;
        if (dom) dom.value = voiceKey;
    }
    function getDuckDb() {
        var v = Number(readSetting(SETTING_KEY_DUCK, DEFAULT_DUCK_DB));
        if (!isFinite(v)) v = DEFAULT_DUCK_DB;
        // Clamp to a sane range so a corrupt setting can't blow eardrums.
        if (v > 0) v = 0;
        if (v < -40) v = -40;
        return v;
    }

    // ────────────────────────────────────────────────────────────────────
    // Sentence grouping — split translated words into sentence-shaped
    // buckets so each Piper inference covers a natural breath unit.
    // Mirrors pipeline's groupIntoSentences() but kept private so the
    // pipeline can swap its grouping algorithm without breaking us.
    // ────────────────────────────────────────────────────────────────────
    function groupSentences(words) {
        var out = [];
        if (!Array.isArray(words) || !words.length) return out;
        var bucket = [];
        for (var i = 0; i < words.length; i++) {
            var w = words[i];
            if (!w || w.isEmoji) continue;
            if (!w.text) continue;
            bucket.push(w);
            var ends = /[\.\?\!…。！？؟]$/.test(String(w.text));
            if (ends || bucket.length >= 14 || i === words.length - 1) {
                out.push({
                    start: bucket[0].start,
                    end:   bucket[bucket.length - 1].end,
                    text:  bucket.map(function (b) { return b.text; }).join(' ')
                });
                bucket = [];
            }
        }
        return out;
    }

    // ────────────────────────────────────────────────────────────────────
    // Audio plumbing
    // ────────────────────────────────────────────────────────────────────
    function getAudioContext() {
        var ACtor = window.AudioContext || window.webkitAudioContext || window.OfflineAudioContext;
        if (!ACtor) throw new Error('no AudioContext');
        // Prefer a live AudioContext (caller reuse); fall back to a 48k
        // OfflineAudioContext for one-off decodes (Node test harness shims).
        if (window.AudioContext) {
            if (!window.__acVoiceDubAC) window.__acVoiceDubAC = new window.AudioContext();
            return window.__acVoiceDubAC;
        }
        return new ACtor(1, 48000, 48000);
    }

    function newAudioBuffer(channels, length, sampleRate) {
        if (typeof AudioBuffer === 'function') {
            try { return new AudioBuffer({ numberOfChannels: channels, length: length, sampleRate: sampleRate }); }
            catch (_) {}
        }
        var OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        if (!OAC) throw new Error('no OfflineAudioContext');
        var ctx = new OAC(channels, Math.max(1, length), sampleRate);
        return ctx.createBuffer(channels, Math.max(1, length), sampleRate);
    }

    // Decode the Piper WAV ArrayBuffer to an AudioBuffer. Piper returns
    // 22050 Hz mono PCM-16 WAV; we just need the samples.
    function decodeWav(arrayBuffer) {
        // Parse minimal WAV header — Piper always emits the same layout, but
        // we read it defensively in case a future Piper build changes.
        var view = new DataView(arrayBuffer);
        if (view.byteLength < 44) throw new Error('wav too small');
        // 'RIFF' magic (little-endian uint32 = 0x46464952)
        if (view.getUint32(0, true) !== 0x46464952) throw new Error('not RIFF');
        if (view.getUint32(8, true) !== 0x45564157) throw new Error('not WAVE');
        // Walk chunks from byte 12 until we find 'data'.
        var p = 12;
        var sampleRate = 22050;
        var bitsPerSample = 16;
        var numChannels = 1;
        var dataOffset = -1;
        var dataLen = 0;
        while (p < view.byteLength - 8) {
            var chunkId = view.getUint32(p, true);
            var chunkSize = view.getUint32(p + 4, true);
            if (chunkId === 0x20746d66) { // 'fmt '
                numChannels = view.getUint16(p + 10, true);
                sampleRate  = view.getUint32(p + 12, true);
                bitsPerSample = view.getUint16(p + 22, true);
            } else if (chunkId === 0x61746164) { // 'data'
                dataOffset = p + 8;
                dataLen = chunkSize;
                break;
            }
            p += 8 + chunkSize;
        }
        if (dataOffset < 0) throw new Error('no data chunk');
        var sampleCount = (dataLen / (bitsPerSample / 8)) / numChannels;
        var buf = newAudioBuffer(numChannels, sampleCount, sampleRate);
        for (var c = 0; c < numChannels; c++) {
            var ch = buf.getChannelData(c);
            var o = dataOffset + c * (bitsPerSample / 8);
            var stride = numChannels * (bitsPerSample / 8);
            for (var i = 0; i < sampleCount; i++) {
                var s = view.getInt16(o + i * stride, true);
                ch[i] = s / 32768;
            }
        }
        return buf;
    }

    // Concatenate per-sentence AudioBuffers into one mono buffer placed at
    // each sentence's original `start` seconds. The TTS sentence may have
    // been time-stretched to fit; we still anchor it by start (Rubberband
    // can over- or undershoot by a few ms — we clamp into the slot).
    function concatBuffers(parts, sampleRate, totalDuration) {
        var total = Math.max(1, Math.ceil(totalDuration * sampleRate));
        var out = newAudioBuffer(1, total, sampleRate);
        var dst = out.getChannelData(0);
        for (var i = 0; i < parts.length; i++) {
            var p = parts[i];
            var startSample = Math.max(0, Math.floor(p.startSec * sampleRate));
            var src = p.buffer.getChannelData(0);
            // Avoid overwriting tail of previous sentence — last-writer-wins
            // is fine because the gap shouldn't realistically overlap.
            for (var j = 0; j < src.length && (startSample + j) < total; j++) {
                dst[startSample + j] = src[j];
            }
        }
        return out;
    }

    // 16-bit PCM mono WAV encoder. Used to write the final mixed PCM into
    // FFmpeg's virtual filesystem as an extra input on the burn path.
    function encodeWav(audioBuffer) {
        var sampleRate = audioBuffer.sampleRate;
        var numChannels = audioBuffer.numberOfChannels;
        var length = audioBuffer.length;
        var bytesPerSample = 2;
        var blockAlign = numChannels * bytesPerSample;
        var dataLen = length * blockAlign;
        var buf = new ArrayBuffer(44 + dataLen);
        var v = new DataView(buf);
        // RIFF / WAVE header
        v.setUint32(0,  0x46464952, true); // 'RIFF'
        v.setUint32(4,  36 + dataLen, true);
        v.setUint32(8,  0x45564157, true); // 'WAVE'
        v.setUint32(12, 0x20746d66, true); // 'fmt '
        v.setUint32(16, 16, true);          // fmt chunk size
        v.setUint16(20, 1, true);           // PCM
        v.setUint16(22, numChannels, true);
        v.setUint32(24, sampleRate, true);
        v.setUint32(28, sampleRate * blockAlign, true);
        v.setUint16(32, blockAlign, true);
        v.setUint16(34, 16, true);          // bits per sample
        v.setUint32(36, 0x61746164, true); // 'data'
        v.setUint32(40, dataLen, true);
        // Interleaved 16-bit samples
        var p = 44;
        var channels = [];
        for (var c = 0; c < numChannels; c++) channels.push(audioBuffer.getChannelData(c));
        for (var i = 0; i < length; i++) {
            for (var c2 = 0; c2 < numChannels; c2++) {
                var s = channels[c2][i];
                if (s > 1) s = 1; else if (s < -1) s = -1;
                v.setInt16(p, (s * 0x7fff) | 0, true);
                p += 2;
            }
        }
        return new Uint8Array(buf);
    }

    // ────────────────────────────────────────────────────────────────────
    // synthesize(words, targetLang, opts) — render the entire translated
    // transcript into ONE AudioBuffer placed on the original timeline.
    //
    // opts: { duration, onProgress, voice, sampleRate }
    //   duration   — total seconds of the original video (clamp tail)
    //   onProgress — (sentenceIdx, total) callback
    //   voice      — override voice key (else getVoice() or first compatible)
    //   sampleRate — output sample rate (default 22050 = Piper native)
    // ────────────────────────────────────────────────────────────────────
    async function synthesize(words, targetLang, opts) {
        opts = opts || {};
        if (!Array.isArray(words) || !words.length) {
            throw new Error('voice-dubbing: no words');
        }
        if (!targetLang) throw new Error('voice-dubbing: targetLang missing');
        if (!isPiperReady()) throw new Error('voice-dubbing: PiperEngine unavailable');

        var voiceKey = opts.voice || getVoice();
        var voice = null;
        if (voiceKey) {
            voice = VOICE_CATALOGUE.find(function (v) { return v.key === voiceKey && v.targetLang === targetLang; }) || null;
        }
        if (!voice) {
            // Fall back to first compatible voice for the target.
            voice = VOICE_CATALOGUE.find(function (v) { return v.targetLang === targetLang; }) || null;
        }
        if (!voice) throw new Error('voice-dubbing: no on-device voice for ' + targetLang);

        var sentences = groupSentences(words);
        if (!sentences.length) throw new Error('voice-dubbing: no sentences');

        var sampleRate = opts.sampleRate || 22050;
        var duration   = Math.max(0, Number(opts.duration) || sentences[sentences.length - 1].end);

        // Render each sentence sequentially (the Piper worker is single-flight).
        var parts = [];
        for (var i = 0; i < sentences.length; i++) {
            var s = sentences[i];
            var targetSec = Math.max(0.1, s.end - s.start);
            var ttsRes = await window.PiperEngine.synthesize(s.text, { lang: voice.langKey });
            var ttsBuf = decodeWav(ttsRes.wav);
            var fitted = await applyTimestretch(ttsBuf, targetSec);
            parts.push({ startSec: s.start, buffer: fitted });
            if (opts.onProgress) {
                try { opts.onProgress(i + 1, sentences.length); } catch (_) {}
            }
        }
        return concatBuffers(parts, sampleRate, duration);
    }

    // ────────────────────────────────────────────────────────────────────
    // applyTimestretch(buffer, targetDurationSec) — fit a TTS sentence into
    // the original word-timing window using Rubberband R3. Formant-preserving
    // by default so a sped-up sentence doesn't chipmunk.
    //
    // Skips the worker round-trip when the rate is already within tolerance
    // — the no-op path returns a fresh copy so callers always get an
    // independent AudioBuffer they can splice without aliasing.
    // ────────────────────────────────────────────────────────────────────
    async function applyTimestretch(buffer, targetDurationSec) {
        if (!buffer || typeof buffer.getChannelData !== 'function') {
            throw new Error('voice-dubbing: applyTimestretch needs an AudioBuffer');
        }
        var srcSec = buffer.length / buffer.sampleRate;
        var tgt = Math.max(0.05, Number(targetDurationSec) || srcSec);
        // Rubberband's `rate` is OUTPUT/INPUT duration. rate < 1 = compress
        // (faster), rate > 1 = expand (slower). We want output = tgt, so
        // rate = tgt / srcSec.
        var rate = srcSec / tgt;
        // Clamp to keep voices intelligible. Anything outside [0.5, 2.0]
        // sounds like a broken effect — better to truncate.
        if (rate < 0.5) rate = 0.5;
        if (rate > 2.0) rate = 2.0;
        if (window.ensureAudioPipeline && !window.AudioPipeline) { try { await window.ensureAudioPipeline(); } catch(_){} }
        if (Math.abs(rate - 1) < 0.01 || !isRubberbandReady()) {
            // No stretch needed (or fallback path). Return a copy so the
            // caller can splice without aliasing the original PCM.
            var copy = newAudioBuffer(buffer.numberOfChannels, buffer.length, buffer.sampleRate);
            for (var c = 0; c < buffer.numberOfChannels; c++) {
                copy.getChannelData(c).set(buffer.getChannelData(c));
            }
            return copy;
        }
        // RubberbandEngine.shift signature: shift(buffer, { semitones, rate,
        // preserveFormants, onProgress }) — returns AudioBuffer.
        // A dubbed phrase is speech: a pure tempo fit goes through WSOLA, which
        // keeps the voice's pulses (the phase vocoder makes it hollow —
        // audio-pipeline.js stretchEngineFor).
        return await window.AudioPipeline.stretch(buffer, {
            semitones: 0,
            rate: rate,
            preserveFormants: true,
            content: 'speech',
            slug: 'auto-captions'
        });
    }

    // ────────────────────────────────────────────────────────────────────
    // mixDownToOriginal(originalBuf, dubBuf, duckDb)
    //   Ducks the original by duckDb (default -15 dB) and sums the dub bus
    //   on top. Both buffers are resampled by zero-stuffing to match the
    //   higher sample rate — for the auto-captions use case the original
    //   video audio is decoded by FFmpeg at whatever rate it has; the dub
    //   is the Piper 22050 Hz bed. We render to the ORIGINAL's sample rate
    //   so the burn FFmpeg invocation doesn't have to resample again.
    //
    //   When the two buffers differ in length we render to the LONGER of
    //   the two and zero-fill the shorter — protects against off-by-one
    //   frames at the tail.
    // ────────────────────────────────────────────────────────────────────
    async function mixDownToOriginal(originalBuf, dubBuf, duckDb) {
        if (!originalBuf || typeof originalBuf.getChannelData !== 'function') {
            throw new Error('voice-dubbing: mixDown needs original AudioBuffer');
        }
        if (!dubBuf || typeof dubBuf.getChannelData !== 'function') {
            throw new Error('voice-dubbing: mixDown needs dub AudioBuffer');
        }
        var duck = (duckDb == null) ? getDuckDb() : Number(duckDb);
        if (!isFinite(duck)) duck = DEFAULT_DUCK_DB;
        var duckGain = Math.pow(10, duck / 20);   // dB → linear

        var sr = originalBuf.sampleRate;
        var numChannels = originalBuf.numberOfChannels || 1;
        // 2026-06-04 DSP fix: was linear-interpolation. Per cross-tool audit,
        // the rationalization "original carries HF, linear is fine on the dub
        // bed" breaks down exactly during dub bursts — the original is ducked
        // -12..-18 dB so the dub bus DOMINATES, and its linear-resampled HF
        // rolloff (~3 dB above 10 kHz on a 22050→44100 upsample) is audible
        // on the downloadable artifact. Replaced with Kaiser-windowed sinc
        // (~90 dB stopband, preserves HF to <0.13 dB) per
        // [[reference_oac_not_hq_resampler]] — OAC would be the obvious
        // upgrade but is empirically bit-identical to linear in Chromium.
        var dubResampled = await resampleHQ(dubBuf.getChannelData(0), dubBuf.sampleRate, sr);
        var length = Math.max(originalBuf.length, dubResampled.length);
        var out = newAudioBuffer(numChannels, length, sr);
        for (var c = 0; c < numChannels; c++) {
            var origCh = originalBuf.getChannelData(Math.min(c, originalBuf.numberOfChannels - 1));
            var dst = out.getChannelData(c);
            for (var i = 0; i < length; i++) {
                var orig = i < origCh.length ? origCh[i] * duckGain : 0;
                var dub  = i < dubResampled.length ? dubResampled[i] : 0;
                var sum = orig + dub;
                // Soft clip — pure hard clip on a -3 dBFS broadcast bed +
                // a hot Piper sentence can crackle. tanh ≈ identity below
                // 0.5 and asymptotes to ±1.
                if (sum > 1 || sum < -1) sum = Math.tanh(sum);
                dst[i] = sum;
            }
        }
        return out;
    }

    // Kaiser-windowed sinc resampler (β=8.6, N=24 taps/side, ~90 dB stopband).
    // Used in mixDownToOriginal — the dub bus resample lands on the file the
    // user downloads. See [[reference_oac_not_hq_resampler]] for why the
    // browser-native OfflineAudioContext is NOT a real upgrade. Identical
    // recipe to public/js/redub-assembly.js:_resampleHQ.
    async function resampleHQ(src, srcRate, dstRate) {
        if (srcRate === dstRate) return new Float32Array(src);
        var TAPS = 24, BETA = 8.6;
        var ratio = srcRate / dstRate;
        var lpCutoff = 0.5 / Math.max(ratio, 1.0);
        function bessI0(x){
            var y = (x / 2) * (x / 2), s = 1, a = 1;
            for (var i = 1; i < 30; i++) { a *= y / (i * i); s += a; if (a < 1e-15) break; }
            return s;
        }
        var I0Beta = bessI0(BETA);
        var dstLen = Math.round(src.length / ratio);
        var out = new Float32Array(dstLen);
        var srcLen = src.length;
        for (var i = 0; i < dstLen; i++) {
            var center = i * ratio;
            var i0 = Math.floor(center);
            var acc = 0;
            for (var k = -TAPS + 1; k <= TAPS; k++) {
                var n = i0 + k;
                if (n < 0 || n >= srcLen) continue;
                var x = (n - center);
                var arg = 2 * Math.PI * lpCutoff * x;
                var sinc = (Math.abs(x) < 1e-9) ? 1 : Math.sin(arg) / arg;
                var t = x / TAPS;
                var w = (Math.abs(t) >= 1) ? 0 : bessI0(BETA * Math.sqrt(1 - t * t)) / I0Beta;
                acc += src[n] * sinc * w * (2 * lpCutoff);
            }
            out[i] = acc;
        }
        return out;
    }

    // Kept as fallback / for diagnostic; not used on the main audio path.
    function resampleLinear(src, srcRate, dstRate) {
        if (srcRate === dstRate) return src;
        var ratio = srcRate / dstRate;
        var outLen = Math.ceil(src.length / ratio);
        var out = new Float32Array(outLen);
        for (var i = 0; i < outLen; i++) {
            var srcIdx = i * ratio;
            var i0 = Math.floor(srcIdx);
            var i1 = Math.min(src.length - 1, i0 + 1);
            var frac = srcIdx - i0;
            out[i] = src[i0] * (1 - frac) + src[i1] * frac;
        }
        return out;
    }

    // ────────────────────────────────────────────────────────────────────
    // buildBurnAudioFilter() — returns the FFmpeg `-filter_complex` snippet
    // the burn pipeline appends so the dub bus mixes over the original.
    //
    // Strategy used by the burn path:
    //   1. We pre-render the FULL mixed track (dub + ducked original) into
    //      a 16-bit WAV via mixDownToOriginal + encodeWav and write it to
    //      FFmpeg's MEMFS as `dub.wav`.
    //   2. The burn invocation adds `-i dub.wav` as an extra input and
    //      maps `[1:a]` (the dub track) instead of `0:a?`.
    //
    // Because the mixing already happened in JS, the FFmpeg filter is the
    // identity — we don't need amix/sidechaincompress. We still return a
    // structured object so the pipeline can swap to an FFmpeg-side ducking
    // strategy later without us shipping a new client.
    // ────────────────────────────────────────────────────────────────────
    function buildBurnAudioFilter() {
        return {
            extraInputs:    ['dub.wav'],      // pre-mixed; ffmpeg-side identity
            audioMap:       '[1:a]',           // burn pipeline replaces `0:a?` with this
            // When a future opts.ffmpegSideMix path lands, populate this with
            // something like amix=inputs=2:weights=`1 0.18`; current is no-op.
            filterChain:    null,
            audioCopy:      false,             // re-encode required (dub.wav is PCM)
        };
    }

    // ────────────────────────────────────────────────────────────────────
    // Public surface
    // ────────────────────────────────────────────────────────────────────
    window.__acVoiceDubbing = {
        isEnabled:           isEnabled,
        setEnabled:          setEnabled,
        getVoice:            getVoice,
        setVoice:            setVoice,
        getDuckDb:           getDuckDb,
        availableVoices:     availableVoices,
        synthesize:          synthesize,
        applyTimestretch:    applyTimestretch,
        mixDownToOriginal:   mixDownToOriginal,
        buildBurnAudioFilter: buildBurnAudioFilter,
        encodeWav:           encodeWav,

        // Internals exposed for the Node test harness.
        _groupSentences:     groupSentences,
        _decodeWav:          decodeWav,
        _resampleLinear:     resampleLinear,
        _resampleHQ:         resampleHQ,
        _newAudioBuffer:     newAudioBuffer,
        _VOICE_CATALOGUE:    VOICE_CATALOGUE,
        _DEFAULT_DUCK_DB:    DEFAULT_DUCK_DB,
    };
})();
